import React from "react";
import { ChevronDown, Upload, MessageSquare, Type, Globe, Settings, Check, Code, Copy } from "lucide-react";

// Product mockups rendered in pure React/CSS rather than screenshots: they stay sharp at
// any size, follow the light/dark theme, and never go stale when the real UI changes
// colours. Extracted from LandingPage so every marketing page can show the product
// instead of describing it in paragraphs.
//
// Every component takes `d` (dark mode) so it can be dropped into any themed page.

function TrainSourcePreview({ d }) {
    const accordions = [
        { icon: Upload, label: "Add files", expanded: true },
        { icon: MessageSquare, label: "Add Q&A" },
        { icon: Type, label: "Add text" },
        { icon: Globe, label: "Add website" },
    ];

    return (
        <div className={`h-full w-full p-4 md:p-5 flex flex-col gap-2.5 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            {accordions.map((a, i) => {
                const Icon = a.icon;
                return (
                    <div
                        key={i}
                        className={`rounded-[14px] border overflow-hidden ${d ? "border-white/[0.06]" : "border-gray-200"}`}
                    >
                        <div className="w-full flex items-center justify-between px-3.5 py-2.5">
                            <div className="flex items-center gap-2.5">
                                <Icon className={`w-4 h-4 ${d ? "text-gray-400" : "text-gray-600"}`} strokeWidth={1.75} />
                                <span className={`text-sm font-semibold ${d ? "text-white" : "text-gray-900"}`}>{a.label}</span>
                            </div>
                            <ChevronDown
                                className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${a.expanded ? "rotate-180" : ""}`}
                            />
                        </div>

                        {a.expanded && (
                            <div className={`px-3.5 pb-3.5 pt-1 border-t ${d ? "border-white/[0.06]" : "border-gray-100"}`}>
                                <div
                                    className={`border-2 border-dashed rounded-[10px] py-4 text-center ${
                                        d ? "border-white/[0.08]" : "border-gray-200"
                                    }`}
                                >
                                    <div className="flex flex-col items-center gap-1">
                                        <Upload className="w-5 h-5 text-gray-400" strokeWidth={1.75} />
                                        <p className={`text-[11px] font-medium ${d ? "text-gray-400" : "text-gray-500"}`}>
                                            Drag & drop files or click to select
                                        </p>
                                        <p className={`text-[10px] ${d ? "text-gray-600" : "text-gray-400"}`}>
                                            PDF, DOC, DOCX, TXT, MD · max 10MB
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function AppearancePreview({ d }) {
    const positions = [
        { label: "Bottom Right", active: true },
        { label: "Bottom Left", active: false },
        { label: "Top Right", active: false },
        { label: "Top Left", active: false },
    ];
    const sizes = [
        { label: "Small", dims: "320 × 480" },
        { label: "Medium", dims: "380 × 560", active: true },
        { label: "Large", dims: "440 × 640" },
    ];

    return (
        <div className={`h-full w-full overflow-hidden ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <div
                className={`h-full m-4 rounded-[16px] border flex flex-col ${
                    d ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200"
                }`}
            >
                {/* Card header */}
                <div className={`px-5 py-3.5 border-b flex items-center gap-3 ${d ? "border-white/[0.06]" : "border-gray-100"}`}>
                    <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center ${d ? "bg-white/[0.04]" : "bg-gray-50"}`}>
                        <Settings className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className={`text-sm font-semibold ${d ? "text-white" : "text-gray-900"}`}>Widget Settings</div>
                        <div className={`text-[11px] ${d ? "text-gray-500" : "text-gray-500"}`}>Customize your chatbot appearance</div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex-1 px-5 py-4 space-y-4 overflow-hidden">
                    {/* Theme */}
                    <div>
                        <div className={`text-[11px] font-medium mb-2 ${d ? "text-gray-300" : "text-gray-700"}`}>Widget Theme</div>
                        <div className="grid grid-cols-2 gap-2.5">
                            <div className={`text-center text-xs py-2.5 rounded-[10px] border-2 font-medium ${d ? "border-white/[0.06] text-gray-500" : "border-gray-200 text-gray-500"}`}>Light</div>
                            <div className={`text-center text-xs py-2.5 rounded-[10px] border-2 font-medium ${d ? "border-blue-500 bg-blue-950 text-blue-300" : "border-blue-500 bg-blue-50 text-blue-700"}`}>Dark</div>
                        </div>
                    </div>

                    {/* Primary color */}
                    <div>
                        <div className={`text-[11px] font-medium mb-2 ${d ? "text-gray-300" : "text-gray-700"}`}>Primary Color</div>
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-[10px] border-2 border-gray-400 flex items-center justify-center bg-black scale-105">
                                <Check className="w-4 h-4 text-white" strokeWidth={3} />
                            </div>
                            <div className={`w-9 h-9 rounded-[10px] border-2 bg-white ${d ? "border-white/[0.06]" : "border-gray-200"}`} />
                            <div className={`w-9 h-9 rounded-[10px] border-2 ${d ? "border-white/[0.06]" : "border-gray-200"}`} style={{ backgroundColor: "#667eea" }} />
                            <span className={`text-xs ml-1 ${d ? "text-gray-500" : "text-gray-500"}`}>or</span>
                            <div className={`w-9 h-9 rounded-[10px] border-2 ${d ? "border-white/[0.06]" : "border-gray-200"}`} style={{ background: "linear-gradient(135deg,#ef4444,#f59e0b,#10b981,#3b82f6,#8b5cf6)" }} />
                        </div>
                    </div>

                    {/* Position */}
                    <div>
                        <div className={`text-[11px] font-medium mb-2 ${d ? "text-gray-300" : "text-gray-700"}`}>Position</div>
                        <div className="grid grid-cols-2 gap-2.5">
                            {positions.map((p) => (
                                <div
                                    key={p.label}
                                    className={`text-center text-xs py-2 rounded-[10px] border-2 font-medium ${
                                        p.active
                                            ? d
                                                ? "border-blue-500 bg-blue-950 text-blue-300"
                                                : "border-blue-500 bg-blue-50 text-blue-700"
                                            : d
                                              ? "border-white/[0.06] text-gray-500"
                                              : "border-gray-200 text-gray-500"
                                    }`}
                                >
                                    {p.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Widget Size */}
                    <div>
                        <div className={`text-[11px] font-medium mb-2 ${d ? "text-gray-300" : "text-gray-700"}`}>Widget Size</div>
                        <div className="grid grid-cols-3 gap-2">
                            {sizes.map((s) => (
                                <div
                                    key={s.label}
                                    className={`text-center py-1.5 rounded-[10px] border-2 ${
                                        s.active
                                            ? d
                                                ? "border-blue-500 bg-blue-950 text-blue-300"
                                                : "border-blue-500 bg-blue-50 text-blue-700"
                                            : d
                                              ? "border-white/[0.06] text-gray-500"
                                              : "border-gray-200 text-gray-500"
                                    }`}
                                >
                                    <div className="text-xs font-medium">{s.label}</div>
                                    <div className={`text-[10px] ${d ? "text-gray-500" : "text-gray-500"}`}>{s.dims}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Border radius slider */}
                    <div>
                        <div className={`text-[11px] font-medium mb-2 ${d ? "text-gray-300" : "text-gray-700"}`}>Border Radius: 16px</div>
                        <div className={`h-2 rounded-full relative ${d ? "bg-white/[0.08]" : "bg-gray-200"}`}>
                            <div className="absolute inset-y-0 left-0 w-2/3 bg-black dark:bg-white rounded-full" />
                            <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-gray-900 shadow" style={{ left: "calc(66% - 8px)" }} />
                        </div>
                        <div className={`flex justify-between text-[10px] mt-1 ${d ? "text-gray-500" : "text-gray-500"}`}>
                            <span>Sharp (0px)</span>
                            <span>Rounded (24px)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AnalyticsPreview({ d }) {
    // The tiles deliberately carry no figures. Real-looking numbers ("1,755", "+12%") read
    // as evidence of live usage on a page whose whole pitch is "trust this with your
    // customers"; a neutral bar reads as "this is the interface", which is all it is.
    // Same reason the sample conversations below carry no timestamps.
    const stats = [
        { label: "Total Interactions", w: "w-14" },
        { label: "Active Chats", w: "w-8" },
        { label: "Avg Response", w: "w-11" },
    ];

    // Two items, because a third is cut off by the card's 4:3 crop and leaves an orphaned
    // status badge hanging at the bottom edge. One resolved and one handed off, so the
    // preview shows both outcomes rather than implying everything gets answered.
    const activity = [
        {
            user: "What time do you close on Sundays?",
            bot: "Sundays we're open 10am–4pm.",
            resolved: true,
        },
        {
            user: "Can I get a refund after 30 days?",
            bot: "Our policy covers 30 days. I'll connect you to support.",
            resolved: false,
        },
    ];

    return (
        <div className={`h-full w-full p-4 flex flex-col gap-3 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            {/* Stat tiles */}
            <div className="grid grid-cols-3 gap-2.5">
                {stats.map((s, i) => (
                    <div
                        key={i}
                        className={`px-3 py-2.5 rounded-[12px] border ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                    >
                        <div className={`text-[10px] font-medium leading-tight mb-1 truncate ${d ? "text-gray-500" : "text-gray-500"}`}>{s.label}</div>
                        <div className="flex items-center h-5">
                            <div className={`h-2.5 rounded-full ${s.w} ${d ? "bg-white/[0.14]" : "bg-gray-300"}`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent activity */}
            <div className={`flex-1 rounded-[12px] border px-3.5 py-3 flex flex-col gap-2.5 overflow-hidden ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}>
                <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${d ? "text-white" : "text-gray-900"}`}>Conversations</span>
                </div>

                {activity.map((a, i) => (
                    <div key={i} className={`flex flex-col gap-1 pb-2.5 ${i < activity.length - 1 ? (d ? "border-b border-white/[0.04]" : "border-b border-gray-200/70") : ""}`}>
                        <div className="flex items-center justify-end">
                            {a.resolved ? (
                                <span className="text-[10px] text-emerald-500 font-medium flex items-center gap-0.5">
                                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                    Resolved
                                </span>
                            ) : (
                                <span className="text-[10px] text-amber-500 font-medium">● Open</span>
                            )}
                        </div>
                        <div className="flex justify-end">
                            <div className={`max-w-[85%] text-[11px] px-2.5 py-1.5 rounded-[10px] ${d ? "bg-white/[0.08] text-gray-200" : "bg-white text-gray-800 border border-gray-200"}`}>
                                {a.user}
                            </div>
                        </div>
                        <div className="flex justify-start">
                            <div className="max-w-[85%] text-[11px] px-2.5 py-1.5 rounded-[10px] bg-blue-500 text-white">
                                {a.bot}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InstallPreview({ d }) {
    return (
        <div className={`h-full w-full overflow-hidden ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <div
                className={`h-full m-4 rounded-[16px] border flex flex-col overflow-hidden ${
                    d ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200"
                }`}
            >
                {/* Card header */}
                <div className={`px-5 py-3.5 border-b flex items-center gap-3 ${d ? "border-white/[0.06]" : "border-gray-100"}`}>
                    <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center ${d ? "bg-white/[0.04]" : "bg-gray-50"}`}>
                        <Code className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className={`text-sm font-semibold ${d ? "text-white" : "text-gray-900"}`}>Integration Code</div>
                        <div className={`text-[11px] ${d ? "text-gray-500" : "text-gray-500"}`}>Add this script tag to your website to enable the chatbot</div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-4 flex-1 flex flex-col gap-3">
                    {/* Black code box */}
                    <div className="bg-black rounded-[14px] overflow-hidden">
                        <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#1a1a1a] border-b border-white/[0.06]">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                            </div>
                            <div className="p-1.5 rounded-[8px] bg-white/[0.06]">
                                <Copy className="w-3.5 h-3.5 text-gray-300" strokeWidth={2} />
                            </div>
                        </div>
                        <div className="px-4 py-3.5">
                            <code className="text-gray-300 font-mono text-xs leading-6 block break-all">
                                {'<script src="https://your-domain.com/widget.js" data-widget-id="YOUR_WIDGET_ID"></script>'}
                            </code>
                        </div>
                    </div>

                    {/* Installation hints grid */}
                    <div className="grid grid-cols-2 gap-2.5 flex-1">
                        <div className={`rounded-[12px] p-3 border ${d ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}>
                            <div className={`text-[11px] font-semibold mb-1.5 ${d ? "text-white" : "text-gray-900"}`}>Where to add it</div>
                            <div className={`text-[10px] leading-snug ${d ? "text-gray-500" : "text-gray-500"}`}>
                                Paste before <span className="font-mono">&lt;/body&gt;</span>, in your footer, or any HTML widget.
                            </div>
                        </div>
                        <div className={`rounded-[12px] p-3 border ${d ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}>
                            <div className={`text-[11px] font-semibold mb-1.5 ${d ? "text-white" : "text-gray-900"}`}>What happens next</div>
                            <div className={`text-[10px] leading-snug ${d ? "text-gray-500" : "text-gray-500"}`}>
                                Widget appears instantly, uses your appearance settings, works on all devices.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { TrainSourcePreview, AppearancePreview, AnalyticsPreview, InstallPreview };
